import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button, Card } from '@orion/ui';
import type { CompanyEnrichmentPersonResponse } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { Timeline } from '../activities/Timeline.js';
import { LogActivityComposer } from '../activities/LogActivityComposer.js';
import { NotesPanel } from '../notes/NotesPanel.js';
import { AttachmentsPanel } from '../attachments/AttachmentsPanel.js';
import { OpenTasksCard } from '../tasks/OpenTasksCard.js';
import { ApiError } from '../../lib/api-client.js';
import { useToast } from '../../lib/toast.js';
import {
  useAddEnrichedPersonAsContact,
  useCompanyEnrichmentQuery,
  useCompanyQuery,
  useCompanySummaryQuery,
  useEnrichCompany,
  useUpdateCompany,
} from './api.js';

type Tab = 'overview' | 'people' | 'tech' | 'activity' | 'tasks' | 'notes' | 'attachments';

function money(value: string): string {
  const n = Number(value);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/** A company IS the customer record once `lifecycleStage` flips
 * (docs/architecture/03 § "a customer is a company") — this one page
 * serves both the prospect and customer view on the design canvas.
 *
 * design/orion-crm-design/rendered-html/CompanyDetail.html shows an exact
 * "240 employees" headcount and "$48M (est.) revenue" — Company only has
 * `sizeBand` (a band like "51-200"), no exact headcount or revenue field,
 * so those are shown honestly (the band) rather than invented precision.
 * The 4-stat header strip (open pipeline/contacts/activities/last contact)
 * is real, from the new GET /companies/:id/summary endpoint. Contacts/
 * Leads/Deals tabs aren't built — they'd need list-by-company endpoints
 * this pass didn't add (Contacts already supports a companyId filter via
 * its many-to-many join and could power that tab later; Leads/Deals don't
 * have an equivalent yet for leads, which only have free-text
 * companyName pre-conversion). */
export function CompanyDetailPage() {
  const { companyId } = useParams({ from: '/_app/companies/$companyId' });
  const { data: company, isLoading, isError } = useCompanyQuery(companyId);
  const { data: summary } = useCompanySummaryQuery(companyId);
  const { data: enrichment, isLoading: enrichmentLoading } = useCompanyEnrichmentQuery(companyId);
  const updateCompany = useUpdateCompany(companyId ?? '');
  const enrichCompany = useEnrichCompany(companyId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const toast = useToast();

  if (isLoading) return <PageHeader title="Loading…" />;
  if (isError || !company) {
    return (
      <>
        <PageHeader title="Company not found" />
        <main className="p-7 text-sm text-text-muted">We couldn't find that company, or you don't have access to it.</main>
      </>
    );
  }

  const isCustomer = company.lifecycleStage === 'customer';

  const markAsCustomer = async () => {
    setError(null);
    try {
      await updateCompany.mutateAsync({ lifecycleStage: 'customer', version: company.version });
    } catch {
      setError('Could not update this company. Please try again.');
    }
  };

  const runEnrich = async () => {
    try {
      await enrichCompany.mutateAsync();
      toast.success('Company profile enriched.');
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not enrich this company.';
      toast.error(detail);
    }
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Companies', to: '/companies' }, { label: company.name }]} />
      <main className="flex flex-1 flex-col gap-3.5 overflow-hidden px-7 pb-6 pt-5">
        {error && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex h-14 shrink-0 items-center gap-4">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-night text-lg font-[650] tracking-[-0.01em] text-amber">
            {company.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">{company.name}</h1>
              <span className={`flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ${isCustomer ? 'bg-success-soft text-success' : 'bg-divider text-text-secondary'}`}>
                <span className={`size-1.5 rounded-full ${isCustomer ? 'bg-success' : 'bg-text-muted'}`} />
                {isCustomer ? 'Customer' : company.lifecycleStage === 'partner' ? 'Partner' : company.lifecycleStage === 'former_customer' ? 'Former customer' : 'Prospect'}
              </span>
              {isCustomer && company.customerSince && (
                <span className="text-[13px] font-semibold text-success">Customer since {new Date(company.customerSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-text-secondary">
              {company.industry ?? 'No industry'}
              {company.sizeBand && ` · ${company.sizeBand} employees`}
              {company.website && (
                <>
                  {' · '}
                  <a href={company.website} className="font-semibold hover:underline">
                    {company.website.replace(/^https?:\/\//, '')}
                  </a>
                </>
              )}
              {company.owner && (
                <>
                  <span className="text-border-strong">|</span>
                  Owner <span className="font-medium text-ink">{company.owner.displayName}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="secondary" onClick={runEnrich} loading={enrichCompany.isPending}>
              {enrichment ? 'Re-enrich' : 'Enrich'}
            </Button>
            {!isCustomer && (
              <Button size="sm" onClick={markAsCustomer} loading={updateCompany.isPending}>
                Mark as customer
              </Button>
            )}
          </div>
        </div>

        {summary && (
          <div className="grid shrink-0 grid-cols-4 rounded-card border border-border bg-surface shadow-card">
            <StatCell label="Open pipeline" value={money(summary.openPipeline.value)} sub={summary.openPipeline.dealCount > 0 ? `${summary.openPipeline.dealCount} opportunit${summary.openPipeline.dealCount === 1 ? 'y' : 'ies'}${summary.openPipeline.topStageName ? ` · ${summary.openPipeline.topStageName}` : ''}` : 'No open opportunities'} bordered />
            <StatCell label="Contacts" value={String(summary.contactsCount)} bordered />
            <StatCell label="Activities · 90 days" value={String(summary.activitiesLast90Days)} bordered />
            <StatCell
              label="Last contact"
              value={summary.lastActivity ? new Date(summary.lastActivity.occurredAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              sub={summary.lastActivity ? `${summary.lastActivity.kind}${summary.lastActivity.actorName ? ` · ${summary.lastActivity.actorName}` : ''}` : 'No activity yet'}
            />
          </div>
        )}

        <nav aria-label="Company sections" className="flex h-9.5 shrink-0 items-end gap-5.5 border-b border-border">
          {(
            [
              ['overview', 'Overview'],
              ['people', 'Key people'],
              ['tech', 'Tech & presence'],
              ['activity', 'Activity'],
              ['tasks', 'Tasks'],
              ['notes', 'Notes'],
              ['attachments', 'Attachments'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={tab === key ? '-mb-px flex h-9.5 items-center border-b-2 border-primary text-sm font-semibold text-ink' : 'flex h-9.5 items-center text-sm font-medium text-text-muted hover:text-ink'}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === 'overview' && (
          <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr_260px] gap-4">
            <Card className="flex flex-col gap-2.5 overflow-y-auto p-4">
              <h2 className="text-[15px] font-semibold text-ink">Company details</h2>
              <dl className="grid grid-cols-[88px_1fr] gap-x-2 gap-y-1.5 text-[13px] leading-4.5">
                <AboutRow label="Industry" value={company.industry} />
                <AboutRow label="Size" value={company.sizeBand} />
                <AboutRow label="Phone" value={company.phone} />
                <AboutRow label="Email" value={company.email} />
                <AboutRow label="Address" value={company.address?.formatted ?? null} />
                <AboutRow label="Owner" value={company.owner?.displayName ?? null} />
                <AboutRow label="Created" value={new Date(company.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
              </dl>
              {company.description && (
                <>
                  <div className="h-px bg-divider" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Description</span>
                  <p className="whitespace-pre-wrap text-[13px] leading-4.5 text-text-secondary">{company.description}</p>
                </>
              )}
              {company.tags.length > 0 && (
                <>
                  <div className="h-px bg-divider" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">Tags</span>
                  <div className="flex flex-wrap gap-1.5">
                    {company.tags.map((t) => (
                      <span key={t.id} className="flex h-5.5 items-center rounded-[6px] border border-border px-2 text-xs font-medium text-text-secondary">
                        {t.name}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </Card>
            <div className="flex min-h-0 flex-col gap-3">
              <LogActivityComposer companyId={company.id} phone={company.phone} />
              <Card className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
                <Timeline companyId={company.id} />
              </Card>
            </div>
            <div className="flex min-h-0 flex-col gap-2.5 overflow-y-auto">
              {enrichment && enrichment.people.length > 0 && (
                <Card className="flex flex-col gap-2.5 p-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-[15px] font-semibold text-ink">Key decision-makers</h2>
                    <button type="button" onClick={() => setTab('people')} className="text-xs font-semibold text-primary-text hover:underline">
                      View all {enrichment.people.length}
                    </button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {enrichment.people.slice(0, 3).map((p) => (
                      <div key={p.id} className="flex items-center gap-2">
                        <span className="flex size-7.5 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-[11px] font-[650] text-white">
                          {p.firstName[0]}
                          {p.lastName[0]}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13px] font-semibold text-ink">{p.fullName}</div>
                          <div className="truncate text-xs text-text-faint">{p.title ?? '—'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              <OpenTasksCard companyId={company.id} />
            </div>
          </div>
        )}

        {tab === 'people' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-5">
            {enrichmentLoading ? (
              <p className="text-sm text-text-muted">Loading…</p>
            ) : !enrichment || enrichment.people.length === 0 ? (
              <EnrichmentEmptyState enriched={Boolean(enrichment)} onEnrich={runEnrich} loading={enrichCompany.isPending} what="any key people" error={enrichment?.lastEnrichError ?? null} />
            ) : (
              <>
                <div className="mb-4 flex items-baseline justify-between">
                  <h2 className="text-[15px] font-semibold text-ink">Key people &amp; decision-makers</h2>
                  <span className="text-xs text-text-faint">Sourced from the company's public site and LinkedIn</span>
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3.5">
                  {enrichment.people.map((p) => (
                    <PersonCard key={p.id} person={p} companyId={company.id} />
                  ))}
                </div>
              </>
            )}
          </Card>
        )}

        {tab === 'tech' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-5">
            {enrichmentLoading ? (
              <p className="text-sm text-text-muted">Loading…</p>
            ) : !enrichment ? (
              <EnrichmentEmptyState enriched={false} onEnrich={runEnrich} loading={enrichCompany.isPending} what="a tech stack or online presence" />
            ) : (
              <div className="grid grid-cols-[1fr_320px] gap-4">
                <div>
                  <h2 className="mb-0.5 text-[15px] font-semibold text-ink">Technology stack</h2>
                  <p className="mb-4 text-xs text-text-faint">Detected from the company's public website</p>
                  {enrichment.technologies.length === 0 ? (
                    <p className="text-sm text-text-muted">No technologies detected.</p>
                  ) : (
                    Object.entries(groupByCategory(enrichment.technologies)).map(([category, items]) => (
                      <div key={category} className="mb-4">
                        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-text-secondary">{category}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {items.map((t) => (
                            <span key={t} className="rounded-chip bg-violet-soft px-2.5 py-1 text-xs font-semibold text-violet">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex flex-col gap-3.5">
                  <Card className="flex flex-col gap-2.5 p-4">
                    <h3 className="text-sm font-semibold text-ink">Online presence</h3>
                    <SocialRow label="LinkedIn" href={enrichment.linkedinUrl} />
                    <SocialRow label="X (Twitter)" href={enrichment.twitterUrl} />
                    <SocialRow label="Facebook" href={enrichment.facebookUrl} />
                    <SocialRow label="Instagram" href={enrichment.instagramUrl} />
                  </Card>
                  {(enrichment.websiteTitle || enrichment.websiteDescription) && (
                    <Card className="flex flex-col gap-1.5 p-4">
                      <h3 className="text-sm font-semibold text-ink">Website</h3>
                      {enrichment.websiteTitle && <div className="text-[13px] font-semibold text-ink">{enrichment.websiteTitle}</div>}
                      {enrichment.websiteDescription && <p className="text-xs leading-4.5 text-text-muted">{enrichment.websiteDescription}</p>}
                    </Card>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        {tab === 'activity' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <Timeline companyId={company.id} />
          </Card>
        )}
        {tab === 'tasks' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <OpenTasksCard companyId={company.id} bare />
          </Card>
        )}
        {tab === 'notes' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <NotesPanel companyId={company.id} />
          </Card>
        )}
        {tab === 'attachments' && (
          <Card className="min-h-0 flex-1 overflow-y-auto p-4">
            <AttachmentsPanel companyId={company.id} />
          </Card>
        )}
      </main>
    </>
  );
}

function StatCell({ label, value, sub, bordered }: { label: string; value: string; sub?: string; bordered?: boolean }) {
  return (
    <div className={`flex flex-col gap-1 px-4.5 py-3 ${bordered ? 'border-r border-divider' : ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="text-[22px] font-[650] leading-none tabular-nums tracking-[-0.02em] text-ink">{value}</span>
        {sub && <span className="text-xs text-text-muted">{sub}</span>}
      </span>
    </div>
  );
}

function AboutRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className="truncate text-ink">{value ?? <span className="text-text-faint">—</span>}</dd>
    </>
  );
}

function groupByCategory(technologies: { category: string; name: string }[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const t of technologies) (groups[t.category] ??= []).push(t.name);
  return groups;
}

function EnrichmentEmptyState({
  enriched,
  onEnrich,
  loading,
  what,
  error,
}: {
  enriched: boolean;
  onEnrich: () => void;
  loading: boolean;
  what: string;
  /** `CompanyEnrichmentResponse.lastEnrichError` — people search and
   * organization enrichment are billed/gated separately on Apollo's side,
   * so a Free-plan key can enrich the company profile while people
   * search alone is unavailable; this is that specific reason, not "the
   * whole enrichment failed" (enrichment having data at all already rules
   * that out). */
  error?: string | null;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center">
      <p className="text-sm text-text-muted">{enriched ? `We didn't find ${what} for this company.` : 'This company hasn’t been enriched yet.'}</p>
      {error && <p className="max-w-md text-xs text-danger">{error}</p>}
      <Button size="sm" onClick={onEnrich} loading={loading}>
        {enriched ? 'Re-enrich' : 'Enrich this company'}
      </Button>
    </div>
  );
}

function SocialRow({ label, href }: { label: string; href: string | null }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-text-secondary">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="font-semibold text-primary-text hover:underline">
          Open
        </a>
      ) : (
        <span className="text-text-faint">Not found</span>
      )}
    </div>
  );
}

function PersonCard({ person, companyId }: { person: CompanyEnrichmentPersonResponse; companyId: string }) {
  const addAsContact = useAddEnrichedPersonAsContact(companyId);
  const toast = useToast();

  const add = async () => {
    try {
      await addAsContact.mutateAsync(person.id);
      toast.success(`${person.fullName} added as a contact.`);
    } catch (err) {
      const detail = err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Could not add this contact.';
      toast.error(detail);
    }
  };

  return (
    <div className="flex flex-col gap-2.5 rounded-control border border-divider p-3.5">
      <div className="flex items-center gap-2.5">
        <span className="flex size-9.5 shrink-0 items-center justify-center rounded-full bg-avatar-indigo text-[13px] font-[650] text-white">
          {person.firstName[0]}
          {person.lastName[0]}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-[650] text-ink">{person.fullName}</div>
          <div className="truncate text-xs text-text-faint">{person.title ?? '—'}</div>
        </div>
        {person.linkedinUrl && (
          <a href={person.linkedinUrl} target="_blank" rel="noreferrer" aria-label={`${person.fullName}'s LinkedIn`} className="flex size-7 shrink-0 items-center justify-center rounded-control border border-border">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="#0a66c2" aria-hidden="true">
              <path d="M20.4 20.4h-3.5v-5.6c0-1.3 0-3-1.9-3s-2.1 1.4-2.1 2.9v5.7H9.3V9h3.4v1.6h.1c.5-.9 1.6-1.9 3.4-1.9 3.6 0 4.2 2.4 4.2 5.5v6.2zM5.3 7.4a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM7 20.4H3.6V9H7v11.4z" />
            </svg>
          </a>
        )}
      </div>
      <div className="text-xs text-text-muted">{person.email ?? 'Email not found'}</div>
      {person.contactId ? (
        <span className="self-start rounded-chip bg-success-soft px-2 py-1 text-xs font-semibold text-success">Added as contact</span>
      ) : (
        <Button size="sm" variant="secondary" onClick={add} loading={addAsContact.isPending} className="self-start">
          + Add as contact
        </Button>
      )}
    </div>
  );
}
